import { Component, Inject, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';

export interface UploadDialogData {
  title: string;
  existingUrl?: string;
  storagePathBase: string; // e.g., "documents/{bookingId}"
  filePrefix: string; // e.g., "timesheet" or "invoice"
  documentName?: string;
}

@Component({
  selector: 'app-upload-document-dialog',
  templateUrl: './upload-document-dialog.component.html',
  styleUrls: ['./upload-document-dialog.component.scss'],
})
export class UploadDocumentDialogComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  selectedFile: File | null = null;
  previewUrl: SafeResourceUrl | null = null;
  documentName: string = '';
  isUploading = false;

  constructor(
    private dialogRef: MatDialogRef<UploadDocumentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UploadDialogData,
    private ux: UxserviceService,
    private sanitizer: DomSanitizer
  ) {
    if (data.existingUrl) {
      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(data.existingUrl);
    }
    if (data.documentName) {
      this.documentName = data.documentName;
    }
  }

  isPdfFile(): boolean {
    if (this.selectedFile) {
      return this.selectedFile.type.includes('pdf');
    }
    if (this.data.existingUrl) {
      return this.data.existingUrl.toLowerCase().includes('.pdf');
    }
    return false;
  }

  openFilePicker() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    if (!(file.type.includes('pdf') || file.type.includes('image'))) {
      this.ux.showError('Please select a PDF or image file.');
      return;
    }

    // Size Restriction: 10MB
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_SIZE) {
      this.ux.showError('File size exceeds the 10MB limit.');
      event.target.value = ''; // Clear the input
      return;
    }

    this.selectedFile = file;
    const objectUrl = URL.createObjectURL(file);
    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
  }

  async uploadDocument() {
    if (!this.selectedFile) {
      // If simply viewing existing and no new file selected, just close
      if (this.previewUrl) {
        this.dialogRef.close();
        return;
      }
      this.ux.showError('Please select a file to upload.');
      return;
    }

    if (!this.documentName || !this.documentName.trim()) {
      this.ux.showError('Document Name is required.');
      return;
    }


    this.isUploading = true;
    try {
      const storage = getStorage();
      const filename = `${this.data.filePrefix}_${Date.now()}_${this.selectedFile.name}`;
      const filePath = `${this.data.storagePathBase}/${filename}`;
      const fileRef = ref(storage, filePath);

      const uploadResult = await uploadBytes(fileRef, this.selectedFile);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      this.dialogRef.close({
        uploadedUrl: downloadURL,
        name: this.documentName?.trim(),
      });
      this.ux.showSuccess('Document uploaded successfully!');

    } catch (err) {
      console.error('Upload error:', err);
      this.ux.showError('Failed to upload document.');
    } finally {
      this.isUploading = false;

    }
  }

  close() {
    this.dialogRef.close();
  }
}
